import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output,
} from '@angular/core';

/**
 * `<app-scroll-top-fab>` — botão flutuante "voltar ao topo". MESMAS definições
 * (ícone `arrow_upward`, estilo `.scroll-top-fab`, limiar, animação 400ms linear
 * via rAF, i18n `submissions.ranking.scroll-to-top`) do FAB da Lista de
 * Submissões (`submissions-list-table-v2`), aqui num componente reutilizável.
 *
 * Aparece quando o container de scroll passa de `threshold` px. Se
 * `scrollContainer` não for passado, detecta o ancestral rolável (ou a página).
 */
@Component({
  selector: 'app-scroll-top-fab',
  templateUrl: './scroll-top-fab.component.html',
  styleUrls: ['./scroll-top-fab.component.scss'],
})
export class ScrollTopFabComponent implements AfterViewInit, OnDestroy {
  /** Rola aparecer só depois de descer este tanto (px). */
  @Input() threshold = 300;
  /** Duração da rolagem programática (ms). */
  @Input() duration = 400;

  /**
   * Container de scroll (opcional). Sem isto, detecta o ancestral rolável.
   * É um SETTER porque o container costuma chegar TARDE: o host o obtém via
   * `@ViewChild` (resolvido só no ngAfterViewInit dele, DEPOIS do nosso) e/ou o
   * elemento vive sob `*ngIf` de dados. Ao mudar, refazemos o alvo/listener se a
   * view já inicializou — senão ficaríamos presos no fallback (página).
   */
  @Input() set scrollContainer(el: HTMLElement | undefined) {
    if (el === this._scrollContainer) { return; }
    this._scrollContainer = el;
    if (this.viewReady) { this.setupTarget(); }
  }
  get scrollContainer(): HTMLElement | undefined { return this._scrollContainer; }
  private _scrollContainer?: HTMLElement;

  /**
   * Emitidos no início/fim da rolagem programática (voltar-ao-topo). O host pode
   * ouvir para suspender trabalho pesado durante a animação — ex.: desanexar a
   * change detection e pular cálculos de layout que rodariam a cada frame de
   * scroll, o que senão starvaria os frames do rAF (jank em DOMs grandes).
   */
  @Output() scrollStart = new EventEmitter<void>();
  @Output() scrollEnd = new EventEmitter<void>();

  public show = false;

  private target?: HTMLElement;
  private useWindow = false;
  private viewReady = false;
  private readonly onScroll = () => this.update();

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setupTarget();
  }

  ngOnDestroy(): void {
    this.teardownTarget();
  }

  // (Re)define o alvo de scroll e (re)registra o listener. Idempotente: solta o
  // listener do alvo anterior antes de trocar.
  private setupTarget(): void {
    this.teardownTarget();
    this.target = this._scrollContainer || this.detectContainer();
    const doc = document;
    this.useWindow = this.target === doc.scrollingElement
      || this.target === doc.documentElement || this.target === doc.body;
    this.zone.runOutsideAngular(() => {
      const evt: EventTarget = this.useWindow ? window : (this.target as HTMLElement);
      evt.addEventListener('scroll', this.onScroll, { passive: true });
    });
    this.update();
  }

  private teardownTarget(): void {
    if (!this.target) { return; }
    const evt: EventTarget = this.useWindow ? window : this.target;
    evt.removeEventListener('scroll', this.onScroll);
  }

  // Ancestral rolável mais próximo do host; senão, o elemento de scroll da página.
  private detectContainer(): HTMLElement {
    let el = this.hostRef.nativeElement.parentElement;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) { return el; }
      el = el.parentElement;
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  }

  private update(): void {
    const shouldShow = (this.target?.scrollTop ?? 0) > this.threshold;
    if (shouldShow !== this.show) {
      this.zone.run(() => { this.show = shouldShow; this.cdr.markForCheck(); });
    }
  }

  // Rola o container ao topo em `duration` ms com velocidade CONSTANTE (linear),
  // via rAF — mesmo comportamento do FAB da Lista de Submissões. Emite
  // scrollStart/scrollEnd para o host suspender trabalho pesado durante a animação.
  public scrollToTop(): void {
    const el = this.target;
    if (!el || el.scrollTop <= 0) { return; }
    const start = el.scrollTop;
    let startTime: number | null = null;
    this.scrollStart.emit();   // em zona (chamado pelo (click)); host pode fazer cdr.detach()
    this.zone.runOutsideAngular(() => {
      const step = (now: number): void => {
        if (startTime === null) { startTime = now; }
        const t = Math.min(1, (now - startTime) / this.duration);
        el.scrollTop = Math.round(start * (1 - t));   // linear
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          this.scrollEnd.emit();   // fora da zona; o host reata/ressincroniza via ngZone.run
        }
      };
      requestAnimationFrame(step);
    });
  }
}
